import { BookmarksRequestMessageSchema, Message, CollectionRegistry, DBUIBlock } from '../types';
import { logger } from '../utils/logger';

/**
 * Handle unified bookmarks management request
 * Supports operations: create, update, delete, getAll, getOne
 * Routes requests to backend collections
 */
export async function handleBookmarksRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      throw new Error(`Collection operation ${collection}.${op} not found`);
    }
    return await handler(params);
  };
  try {
    const request = BookmarksRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;
    const bookmarkId = requestData?.id;
    const userId = requestData?.userId;
    const threadId = requestData?.threadId;
    const name = requestData?.name;
    const description = requestData?.description;
    const uiblock = requestData?.uiblock;
    // Query operation fields
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const sort = requestData?.sort;

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, userId, threadId, name, description, uiblock, executeCollection, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, bookmarkId, threadId, name, description, uiblock, executeCollection, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, bookmarkId, executeCollection, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, executeCollection, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, bookmarkId, executeCollection, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, filters, limit, sort, executeCollection, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle bookmarks request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create bookmark operation
 */
async function handleCreate(
  id: string,
  userId: string | undefined,
  threadId: string | undefined,
  name: string | undefined,
  description: string | undefined,
  uiblock: DBUIBlock | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!userId) {
    sendResponse(id, {
      success: false,
      error: 'userId is required'
    }, sendMessage, clientId);
    return;
  }

  if (!uiblock) {
    sendResponse(id, {
      success: false,
      error: 'UIBlock data is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('bookmarks', 'create', { userId, threadId, name, description, uiblock });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Bookmark created successfully'
    }, sendMessage, clientId);

    logger.info(`Bookmark created: ID ${result.data.id}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create bookmark'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update bookmark operation
 */
async function handleUpdate(
  id: string,
  bookmarkId: number | undefined,
  threadId: string | undefined,
  name: string | undefined,
  description: string | undefined,
  uiblock: DBUIBlock | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!bookmarkId) {
    sendResponse(id, {
      success: false,
      error: 'Bookmark ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('bookmarks', 'update', { id: bookmarkId, threadId, name, description, uiblock });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Bookmark updated successfully'
    }, sendMessage, clientId);

    logger.info(`Bookmark updated: ID ${bookmarkId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update bookmark'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete bookmark operation
 */
async function handleDelete(
  id: string,
  bookmarkId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!bookmarkId) {
    sendResponse(id, {
      success: false,
      error: 'Bookmark ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('bookmarks', 'delete', { id: bookmarkId });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: 'Bookmark deleted successfully'
    }, sendMessage, clientId);

    logger.info(`Bookmark deleted: ID ${bookmarkId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete bookmark'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all bookmarks operation
 */
async function handleGetAll(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('bookmarks', 'getAll', {});

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Retrieved ${result.count} bookmarks`
    }, sendMessage, clientId);

    logger.info(`Retrieved all bookmarks (count: ${result.count})`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get bookmarks'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get one bookmark operation
 */
async function handleGetOne(
  id: string,
  bookmarkId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!bookmarkId) {
    sendResponse(id, {
      success: false,
      error: 'Bookmark ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('bookmarks', 'getOne', { id: bookmarkId });

    sendResponse(id, {
      success: true,
      data: result.data,
      message: `Retrieved bookmark ID ${bookmarkId}`
    }, sendMessage, clientId);

    logger.info(`Retrieved bookmark: ID ${bookmarkId}`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get bookmark'
    }, sendMessage, clientId);
  }
}

/**
 * Handle query bookmarks operation with filters
 */
async function handleQuery(
  id: string,
  filters: any,
  limit: number | undefined,
  sort: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('bookmarks', 'query', {
      filters: filters || {},
      limit,
      sort
    });

    sendResponse(id, {
      success: true,
      data: result.data,
      count: result.count,
      message: `Query returned ${result.count} bookmarks`
    }, sendMessage, clientId);

    logger.info(`Query returned ${result.count} bookmarks`);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to query bookmarks'
    }, sendMessage, clientId);
  }
}

/**
 * Send bookmarks response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any; count?: number; message?: string },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'BOOKMARKS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'user',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
